import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DataList } from "@/common/DataList";
import { handlePeriod } from "@/shared/utils/handle-contacts-null";
import type { ActivitiesTabFormValues } from "@/entities/activities-tab-content/model";
import { EducationField } from "./EducationField";

type Props = {
  personId: string;
  isEditMode: boolean;
  handleEditMode: () => void;
  onSubmit: (data: ActivitiesTabFormValues) => void;
  disabled?: boolean;
};

export const EducationsList = ({
  personId,
  isEditMode,
  handleEditMode,
  onSubmit,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const { control, getValues, trigger } = useFormContext<ActivitiesTabFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "educations",
  });

  const onBlur = async () => {
    const isValid = await trigger("educations");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  const onChange = () => {
    trigger("educations");
  };

  const addHandler = () => {
    if (!isEditMode) {
      handleEditMode();
    }

    setTimeout(() => {
      append({
        id: undefined,
        place: "",
        personId: personId,
        major: "",
        educationStart: "",
        educationEnd: "",
        additional: "",
      });
    }, 0);
  };

  const removeHandler = async (index: number) => {
    remove(index);

    const isValid = await trigger("professionalActivities");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  return (
    <DataList
      data={fields.map((item) => ({
        id: item?.id ?? "",
        label: item?.place ?? "",
        subLabel: handlePeriod(item?.educationStart, item?.educationEnd),
        value: item?.major ?? "",
        about: item?.additional ?? "",
      }))}
      onAddClick={addHandler}
      onTrashClick={removeHandler}
      isEdit={isEditMode}
      title={t("contactsPage.activities.education", { count: 2 })}
      editNode={(index) => <EducationField index={index} onBlur={onBlur} onChange={onChange} />}
      disabled={disabled}
      hint={isEditMode ? t("contactsPage.editEducationHint") : undefined}
    />
  );
};
